import React, {useCallback, useContext, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {clearSelection} from 'src/components/data-table-selection';
import {YesNoFilter} from 'src/components/filters';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {AccountSortField} from 'src/openapi/admin/models/AccountSortField';
import {SortDirection, SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {SortHeaderFilter} from '@pb/kit-component-filters';

import {COLUMNS, FILTERING_WARNING, HEADERS, SELECTION_KEY, SORTING_WARNING} from '../../constants';
import {
    accountSearchResultStateActions,
    accountSearchResultStateSelector,
} from '../../slices/account-search-result-state.slice';
import {accountSearchResultResultSelector} from '../../slices/account-search-result.slice';

import {HeaderSkeleton} from './header-skeleton';

type Props = {
    totalCount: number,
};

const SsoMandatoryController = ({totalCount}: Props) => {
    const dispatch = useDispatch();
    const {setErrorMessage} = useContext(MessageContext);
    const cacheKeys = useSelector(accountSearchResultStateSelector);

    const [ssoMandatory, toggleSsoMandatory] = useState(
        cacheKeys.filters.ssoMandatory as boolean | undefined,
    );

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            toggleSsoMandatory(cacheKeys.filters.ssoMandatory as boolean | undefined);
        }
    }, cacheKeys.filters.ssoMandatory);

    const sort = useCallback((direction: ('ASC' | 'DESC')) => {
        if (totalCount > MAX_SELECTED_ENTITIES) {
            setErrorMessage(SORTING_WARNING);
            return;
        }

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setSortCriteria({
            direction: direction as SortDirection,
            field: AccountSortField.SSO_MANDATORY,
        }));
    }, [totalCount, dispatch, setErrorMessage]);

    const applyFilter = useCallback(() => {
        const {ssoMandatory: _prevValue, ...otherFilters} = cacheKeys.filters;
        dispatch(clearSelection(SELECTION_KEY));

        if (ssoMandatory !== undefined) {
            dispatch(accountSearchResultStateActions.setFilters({
                ...otherFilters,
                ssoMandatory,
            }));
        } else {
            dispatch(accountSearchResultStateActions.setFilters(otherFilters));
        }
    }, [ssoMandatory, dispatch, cacheKeys.filters]);

    const clearFilter = useCallback(() => {
        const {ssoMandatory: _prevValue, ...otherFilters} = cacheKeys.filters;
        toggleSsoMandatory(undefined);

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setFilters(otherFilters));
    }, [dispatch, cacheKeys.filters]);

    const cancelFilter = useCallback(() => {
        toggleSsoMandatory(cacheKeys.filters.ssoMandatory as boolean | undefined);
    }, [cacheKeys.filters]);


    return (
        <SortHeaderFilter
            filterActive={cacheKeys.filters?.ssoMandatory !== undefined}
            sortable={true}
            preventFilterOpening={totalCount > MAX_SELECTED_ENTITIES
                ? () => setErrorMessage(FILTERING_WARNING)
                : undefined}
            caption={HEADERS[COLUMNS.SSO_MANDATORY]}
            direction={cacheKeys.sortCriteria.field === AccountSortField.SSO_MANDATORY
                ? cacheKeys.sortCriteria.direction
                : undefined}
            defaultDirection={SortDirections.ASC}
            onSort={sort}
            onApply={applyFilter}
            onReset={clearFilter}
            onCancel={cancelFilter}>
            <YesNoFilter value={ssoMandatory} onChange={toggleSsoMandatory} label="SSO Mandatory"/>
        </SortHeaderFilter>
    );
};

export const SsoMandatoryHeader = () => {
    const result = useSelector(accountSearchResultResultSelector);

    return result ? (
        <SsoMandatoryController
            totalCount={result.totalCount}
        />
    ) : (
        <HeaderSkeleton columnId={COLUMNS.SSO_MANDATORY}/>
    );
};
